import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton } from "@/components/Brand";
import { COURSE_CATEGORIES, CONTACT, TRAINERS } from "@/lib/data";
import { submitEnquiry } from "@/lib/submitEnquiry";

const empty = {
  name: "",
  email: "",
  phone: "",
  category: "",
  course: "",
  preferred_contact: "",
  message: "",
  company: "", // honeypot
};

const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const changeCategory = (e) => setForm((f) => ({ ...f, category: e.target.value, course: "" }));

  const activeCategory = useMemo(
    () => COURSE_CATEGORIES.find((c) => c.name === form.category),
    [form.category]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source: "contact" });
      toast.success("Thank you, your message has been sent. We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      if (err && err.code === "no-php") {
        toast.error(`Contact form works only on the live site. In the meantime, please email ${CONTACT.email}.`);
      } else if (err && err.code === "rate-limit") {
        toast.error("Too many submissions from this network. Please try again later.");
      } else {
        toast.error(`Something went wrong. Please try again or email ${CONTACT.email}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/20 focus:border-gold text-white placeholder:text-white/40 py-3 px-1 outline-none transition-colors duration-300 font-manrope";

  return (
    <section className="pt-40 md:pt-52 pb-32 bg-navy">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-20">
          <Overline className="mb-5 block">Contact Us</Overline>
          <MaskedLines
            className="font-playfair uppercase leading-[0.95] tracking-tight"
            lineClassName="text-6xl md:text-8xl"
            lines={[<span key="1" className="text-white">Let&apos;s</span>, <span key="2" className="text-gold">Talk.</span>]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <Reveal className="lg:col-span-2 space-y-10">
            <p className="text-white/65 text-lg leading-relaxed max-w-sm">
              Have a question about one of our courses or ready to start your training journey? Get in touch and our team will help you take the next step.
            </p>
            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 shrink-0 rounded-full border border-gold/40 flex items-center justify-center">
                  <Mail size={20} strokeWidth={1.3} className="text-gold" />
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gold mb-1">Email</p>
                  <p className="text-white/85">{CONTACT.email}</p>
                </div>
              </div>

              {/* Phone — both trainers listed with names */}
              <div className="flex items-start gap-5" data-testid="contact-phone-block">
                <div className="w-12 h-12 shrink-0 rounded-full border border-gold/40 flex items-center justify-center mt-1">
                  <Phone size={20} strokeWidth={1.3} className="text-gold" />
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gold mb-2">Phone</p>
                  <ul className="space-y-1.5">
                    {TRAINERS.map((t) => (
                      <li key={t.name} className="flex flex-wrap items-baseline gap-x-3">
                        <span className="text-white/60 text-sm">{t.name.split(" ")[0]}</span>
                        <a
                          href={`tel:${t.phone.replace(/\s/g, "")}`}
                          data-testid={`contact-phone-${t.name.split(" ")[0].toLowerCase()}`}
                          className="text-white/85 hover:text-gold transition-colors"
                        >
                          {t.phone}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 shrink-0 rounded-full border border-gold/40 flex items-center justify-center">
                  <MapPin size={20} strokeWidth={1.3} className="text-gold" />
                </div>
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gold mb-1">Location</p>
                  <p className="text-white/85">{CONTACT.location}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="bg-navy-surface border border-white/10 p-8 md:p-12 space-y-7">
              {/* Honeypot */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
                onChange={change("company")}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <input data-testid="contact-name-input"  className={field} placeholder="Full name *"    value={form.name}  onChange={change("name")} />
                <input data-testid="contact-email-input" className={field} placeholder="Email address *" type="email" value={form.email} onChange={change("email")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <input data-testid="contact-phone-input" className={field} placeholder="Phone number"    value={form.phone} onChange={change("phone")} />
                <select
                  data-testid="contact-category-select"
                  className={`${field} appearance-none cursor-pointer text-white/70`}
                  value={form.category}
                  onChange={changeCategory}
                >
                  <option value="" className="bg-navy-surface">Course of interest</option>
                  {COURSE_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.name} className="bg-navy-surface">{c.name}</option>
                  ))}
                </select>
              </div>
              {activeCategory && activeCategory.options.length > 0 && (
                <select
                  data-testid="contact-course-select"
                  className={`${field} appearance-none cursor-pointer text-white/70`}
                  value={form.course}
                  onChange={change("course")}
                >
                  <option value="" className="bg-navy-surface">Select a specific course (optional)</option>
                  {activeCategory.options.map((c) => (
                    <option key={c.id} value={c.name} className="bg-navy-surface">{c.name}</option>
                  ))}
                </select>
              )}
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/50 mb-3">Preferred Contact Method</p>
                <div className="flex flex-wrap gap-2">
                  {CONTACT_METHODS.map((m) => {
                    const active = form.preferred_contact === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        data-testid={`contact-prefer-${m.toLowerCase()}`}
                        onClick={() => setForm((f) => ({ ...f, preferred_contact: active ? "" : m }))}
                        className={`px-4 py-2 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                          active
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-white/20 text-white/70 hover:border-gold/60 hover:text-gold"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
              <textarea data-testid="contact-message-input" rows={4} className={`${field} resize-none`} placeholder="Your message *" value={form.message} onChange={change("message")} />
              <GoldButton type="submit" disabled={loading} data-testid="contact-submit-button" className="w-full md:w-auto disabled:opacity-60">
                {loading ? "Sending..." : (<>Send Message <Send size={16} strokeWidth={1.5} /></>)}
              </GoldButton>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
